import React from "react";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router";
import { Button, Callout, Card, Form, LoadingDots, Page } from "@sscs/ui";
import { SaveIcon } from "@sscs/ui/icons";
import ClientConfiguration from "../../components/ClientConfiguration/ClientConfiguration";
import ConfirmDisableSSO from "../../components/ConfirmDisableSSO/ConfirmDisableSSO";
import ConfirmSave from "../../components/ConfirmSave/ConfirmSave";
import LocalConfigurationPanel from "../../components/LocalConfigurationPanel/LocalConfigurationPanel";
import OrgRoleMapping from "../../components/OrgRoleMapping/OrgRoleMapping";
import ProviderConfiguration from "../../components/ProviderConfiguration/ProviderConfiguration";
import RestrictedAccess from "../../components/RestrictedAccess";
import SamlProviderConfiguration from "../../components/SamlProviderConfiguration/SamlProviderConfiguration";
import SignatureVerification from "../../components/SignatureVerification/SignatureVerification";
import UserMapping from "../../components/UserMapping/UserMapping";
import { useSSOConfiguration } from "../../hooks";
import { Protocol, UserDirectoryOutletContext } from "../../types";
import styles from "./userDirectory.module.scss";

type ProtocolRadio = {
  value: Protocol;
  label: string;
  id: string;
};

const protocols: ProtocolRadio[] = [
  {
    value: "local",
    label: "Local login",
    id: "local",
  },
  {
    value: "sso-oidc",
    label: "OIDC",
    id: "oidc",
  },
  {
    value: "sso-saml",
    label: "SAML",
    id: "saml",
  },
];

const UserDirectory = (): React.JSX.Element => {
  const { protocolForm } = useOutletContext<UserDirectoryOutletContext>();
  const ssoConfiguration = useSSOConfiguration(protocolForm);
  if (ssoConfiguration.isLoading) return <LoadingDots />;

  return (
    <RestrictedAccess>
      <Page.Body className={styles["sso-body"]}>
        {!ssoConfiguration.isSaveDisabled && (
          <Page.FloatingActions>
            <Callout
              type="error"
              title="You have unsaved changes"
              variant="inline"
            />
            <Button
              color="primary"
              disabled={ssoConfiguration.isSaveDisabled}
              onClick={ssoConfiguration.onSubmit}
            >
              <SaveIcon />
              Save
            </Button>
          </Page.FloatingActions>
        )}
        <Card.Card>
          <Card.Title>SSO protocol</Card.Title>
          <Card.Content className={styles["protocol-content"]}>
            <p className={styles.description}>
              You can set up SSO for several different protocols. Only one
              protocol can be active at any time. If you select another
              protocol, we&apos;ll keep your previous settings so you can easily
              switch back.
            </p>
            <FormProvider {...ssoConfiguration.protocolForm}>
              <Form.Form>
                <Form.RadioGroup
                  orientation="horizontal"
                  name="protocol"
                  items={protocols.map((protocol) => ({
                    id: protocol.id,
                    value: protocol.value,
                    label: protocol.label,
                  }))}
                />
              </Form.Form>
            </FormProvider>
            {ssoConfiguration.protocol === "local" && (
              <LocalConfigurationPanel />
            )}
          </Card.Content>
        </Card.Card>
        {ssoConfiguration.protocol === "sso-oidc" && (
          <FormProvider {...ssoConfiguration.methods}>
            <Form.Form className={styles["user-directory-form"]}>
              <ClientConfiguration />
              <ProviderConfiguration
                claimSource={ssoConfiguration.claimSource}
              />
              <SignatureVerification />
              <UserMapping />
              <OrgRoleMapping />
            </Form.Form>
          </FormProvider>
        )}
        {ssoConfiguration.protocol === "sso-saml" && (
          <FormProvider {...ssoConfiguration.methods}>
            <Form.Form className={styles["user-directory-form"]}>
              <SamlProviderConfiguration />
              <UserMapping />
              <OrgRoleMapping />
            </Form.Form>
          </FormProvider>
        )}
        {ssoConfiguration.protocol === "local" ? (
          <ConfirmDisableSSO
            isOpen={ssoConfiguration.isConfirmSaveOpen}
            onClose={ssoConfiguration.closeConfirmSaveDialog}
            onConfirm={ssoConfiguration.handleConfirm}
          />
        ) : (
          <ConfirmSave
            isOpen={ssoConfiguration.isConfirmSaveOpen}
            onClose={ssoConfiguration.closeConfirmSaveDialog}
            onConfirm={ssoConfiguration.handleConfirm}
          />
        )}
      </Page.Body>
    </RestrictedAccess>
  );
};

export default UserDirectory;
